/**
 * ELF-CIRCLE — Enquiries (admin only).
 *
 * Every "book a consultation" submission captured from the public site, drawn
 * in the site's own language rather than as a bolted-on dashboard: a film band
 * with the nav over it, reach stats, filter chips, and rows that expand the way
 * the events wall does.
 *
 * Admin-gated on both sides — the API rejects non-admins with 403, and this
 * shows a sign-in prompt rather than an empty list, so it can never read as
 * "no enquiries" when it's really "not signed in".
 *
 * Once HubSpot is connected the CRM becomes the working surface; this stays as
 * the record of what was captured.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { apiHeaders, config } from '../../../../auth/config';
import { useAuth } from '../../../../auth';
import { isAdmin, route } from '../../../../../shared/utils';
import bandImage from '../../../../../assets/elf/film-festival.png';

interface Enquiry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  interest: string | null;
  message: string | null;
  source: string | null;
  pageUrl: string | null;
  status: string;
  createdAt: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const EnquiriesInner: React.FC = () => {
  const { user, token } = useAuth();
  const canRead = Boolean(token) && isAdmin(user);

  const [rows, setRows] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('All');
  const [open, setOpen] = useState<string | null>(null);
  /** A rejected token, as opposed to a retryable transport failure. */
  const [expired, setExpired] = useState(false);

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    setExpired(false);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/consultation?limit=200`, {
        headers: apiHeaders({ Authorization: `Bearer ${token}` }),
      });
      // A rejected token is not retryable: the stored session is dead, so show
      // the sign-in gate rather than a Try again that can never succeed.
      if (res.status === 401 || res.status === 403) {
        setExpired(true);
        return;
      }
      if (!res.ok) throw new Error(`Could not load enquiries (HTTP ${res.status})`);
      const body = await res.json();
      setRows(body.consultations ?? []);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load enquiries');
    } finally {
      setLoading(false);
    }
  }, [canRead, token]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Filter options are derived from the data, so they never list an empty bucket. */
  const interests = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach((r) => r.interest && seen.add(r.interest));
    return ['All', ...Array.from(seen).sort()];
  }, [rows]);

  const visible = useMemo(
    () => (filter === 'All' ? rows : rows.filter((r) => r.interest === filter)),
    [rows, filter],
  );

  const stats = useMemo(() => {
    const since = Date.now() - WEEK_MS;
    const thisWeek = rows.filter((r) => new Date(r.createdAt).getTime() >= since).length;
    const fresh = rows.filter((r) => r.status === 'new').length;
    const tally = new Map<string, number>();
    rows.forEach((r) => r.interest && tally.set(r.interest, (tally.get(r.interest) ?? 0) + 1));
    const top = [...tally.entries()].sort((a, b) => b[1] - a[1])[0];
    return [
      { n: String(rows.length), l: 'Total enquiries', d: 'all time' },
      { n: String(fresh), l: 'Awaiting reply', d: 'status: new' },
      { n: String(thisWeek), l: 'Last 7 days', d: 'newly captured' },
      { n: top ? String(top[1]) : '—', l: top ? top[0] : 'Top interest', d: top ? 'most requested' : 'nothing yet' },
    ];
  }, [rows]);

  return (
    <div className="elf-page">
      <div className="elf-page__panel">
        {/* Film band — same treatment as Who We Are */}
        <div className="elf-page__bleed elf-enq__band">
          <img className="bandimg" src={bandImage} alt="" />
          <div className="elf-enq__scrim" />
          <div
            className="elf-page__col"
            style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <ElfPageNav current="/enquiries" onFilm />
            <div className="wnav-onfilm" style={{ padding: '20px var(--pgut) 0' }}>
              <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Enquiries']} />
            </div>
            <div
              style={{
                marginTop: 'auto',
                padding: '0 var(--pgut) 36px',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 32,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <span
                  className="eyebrow"
                  style={{
                    alignSelf: 'flex-start',
                    borderColor: 'rgba(255,255,255,.28)',
                    background: 'rgba(255,255,255,.1)',
                    color: 'rgba(255,255,255,.8)',
                  }}
                >
                  <i />
                  Admin · consultation requests
                </span>
                <h1 className="elf-page__h1 elf-page__h1--film" style={{ fontSize: 'clamp(40px,4.4vw,66px)' }}>
                  Who asked <span style={{ color: 'var(--acc-hero)' }}>to talk.</span>
                </h1>
              </div>
              {canRead && (
                <button
                  className="cta mag"
                  type="button"
                  onClick={() => void load()}
                  disabled={loading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 9,
                    font: "700 12px 'Archivo',sans-serif",
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    background: 'var(--acc-hero)',
                    color: 'var(--on-acc-hero)',
                    padding: '13px 24px',
                    border: 0,
                    borderRadius: 99,
                    cursor: 'pointer',
                  }}
                >
                  {loading ? <Loader2 size={14} className="elf-edit__spin" /> : <RefreshCw size={14} />}
                  {loading ? 'Loading' : 'Refresh'}
                </button>
              )}
            </div>
          </div>
        </div>

        {!canRead || expired ? (
          <div className="elf-enq__note" style={{ marginTop: 44 }}>
            <span className="colhead">{expired ? 'Session expired' : 'Restricted'}</span>
            <p className="wbody" style={{ fontSize: 16, margin: '10px 0 20px' }}>
              {expired
                ? 'Your sign-in is no longer valid. Sign in again to read the enquiries.'
                : 'Enquiries carry personal contact details, so this page is admin-only. Sign in with an admin account to read them.'}
            </p>
            <Link className="pillcta" to={route('/auth/login')} state={{ from: { pathname: '/enquiries' } }}>
              Sign in <span>→</span>
            </Link>
          </div>
        ) : error ? (
          <div className="elf-enq__note" style={{ marginTop: 44, borderColor: 'var(--sig)' }}>
            <span className="colhead" style={{ color: 'var(--sig)' }}>
              Could not load
            </span>
            <p className="wbody" style={{ fontSize: 15, margin: '10px 0 18px' }}>
              {error}
            </p>
            <button className="pillghost" type="button" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="elf-enq__stats">
              {stats.map((s) => (
                <div className="statx" key={s.l}>
                  <span className="statn">{s.n}</span>
                  <span className="statl">{s.l}</span>
                  <span className="wtag" style={{ fontSize: 12 }}>
                    {s.d}
                  </span>
                </div>
              ))}
            </div>

            {rows.length > 0 && (
              <div className="elf-enq__filters">
                <span className="colhead" style={{ marginRight: 6 }}>
                  Filter
                </span>
                {interests.map((i) => (
                  <button
                    key={i}
                    type="button"
                    className={i === filter ? 'fchip on' : 'fchip'}
                    onClick={() => setFilter(i)}
                  >
                    {i}
                  </button>
                ))}
                <span className="live" style={{ marginLeft: 'auto' }}>
                  <i />
                  {visible.length} shown
                </span>
              </div>
            )}

            {rows.length === 0 && !loading ? (
              <div className="elf-enq__note">
                <span className="colhead">Nothing yet</span>
                <p className="wbody" style={{ fontSize: 15, margin: '10px 0 0' }}>
                  No consultation requests have come in. Submit one from any Book a call button and
                  it&rsquo;ll land here.
                </p>
              </div>
            ) : (
              <div className="elf-enq__list">
                {visible.map((r) => {
                  const d = new Date(r.createdAt);
                  const isOpen = open === r.id;
                  return (
                    <div className={isOpen ? 'enqrow open' : 'enqrow'} key={r.id}>
                      <button
                        type="button"
                        className="enqhead"
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : r.id)}
                      >
                        <span className="enqdate">
                          <b>{String(d.getDate()).padStart(2, '0')}</b>
                          <span>{MONTHS[d.getMonth()]}</span>
                        </span>
                        <span className="enqwho">
                          <b>{r.name}</b>
                          <span>{r.email}</span>
                        </span>
                        <span className="enqtags">
                          {r.interest && <span className="bfchip">{r.interest}</span>}
                          <span
                            className={r.status === 'new' ? 'enqstatus enqstatus--new' : 'enqstatus'}
                          >
                            {r.status}
                          </span>
                        </span>
                        <span className="enqcaret">⌄</span>
                      </button>

                      <div className="enqbody">
                        <div>
                          <div className="enqbody__inner">
                            <div>
                              {r.message && (
                                <div className="enqfield">
                                  <span className="colhead">Message</span>
                                  <span>{r.message}</span>
                                </div>
                              )}
                              <div className="enqfield">
                                <span className="colhead">Came from</span>
                                <span>
                                  {(r.source ?? 'unknown').replace(/\./g, ' · ')}
                                  {r.pageUrl ? ` — ${r.pageUrl}` : ''}
                                </span>
                              </div>
                              <div className="enqactions">
                                <a className="pillcta" href={`mailto:${r.email}`}>
                                  Reply by email <span>→</span>
                                </a>
                                {r.phone && (
                                  <a className="pillghost" href={`tel:${r.phone}`}>
                                    Call
                                  </a>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="enqfield">
                                <span className="colhead">Phone</span>
                                <span>{r.phone || '—'}</span>
                              </div>
                              <div className="enqfield">
                                <span className="colhead">Company</span>
                                <span>{r.company || '—'}</span>
                              </div>
                              <div className="enqfield">
                                <span className="colhead">Received</span>
                                <span>{d.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const Enquiries: React.FC = () => (
  <ElfShell>
    <EnquiriesInner />
  </ElfShell>
);

export default Enquiries;
